// Client-side wrapper around /api/search/arxiv and /api/papers/import.

export async function searchArxiv({ q, days, max = 30, signal }) {
  const params = new URLSearchParams({ q });
  if (days) params.set('days', String(days));
  if (max)  params.set('max', String(max));

  const res = await fetch(`/api/search/arxiv?${params.toString()}`, { signal });
  const body = await res.json().catch(() => null);

  // Cloudflare's shared egress hits both arXiv and OpenAlex/SS per-IP daily
  // budgets, so the server can come back with either a 429 or an empty
  // result for entirely valid queries. Both cases get the same client-side
  // rescue: hit OpenAlex from the user's own browser, where the free-tier
  // budget is per-home-IP and almost always fresh.
  const serverThrottled = res.status === 429 || res.status === 503
                         || body?.error?.code === 'rate_limited';
  const serverEmpty = body?.ok && (body.data?.count ?? 0) === 0;

  if (serverThrottled || serverEmpty) {
    try {
      const direct = await searchOpenAlexFromClient({ q, days, max, signal });
      if (direct.count > 0) return direct;
    } catch { /* keep going — we'll surface the original outcome */ }
  }

  if (!res.ok || !body?.ok) {
    const err = new Error(body?.error?.message ?? `arxiv search ${res.status}`);
    err.code = body?.error?.code ?? 'http_error';
    err.status = res.status;
    throw err;
  }
  return body.data; // { query, days, items, count }
}

async function searchOpenAlexFromClient({ q, days, max, signal }) {
  const params = new URLSearchParams({
    search: q,
    'per-page': String(Math.min(Math.max(max, 1), 50)),
    select: 'id,title,abstract_inverted_index,authorships,publication_date,ids,primary_location',
    mailto: 'ai.wooni.ai@gmail.com',
  });
  if (days && Number.isFinite(days)) {
    const now = new Date();
    const from = new Date(now.getTime() - days * 86400 * 1000);
    const fmt = (d) => d.toISOString().slice(0, 10);
    params.set('filter', `from_publication_date:${fmt(from)},to_publication_date:${fmt(now)}`);
  }
  const res = await fetch(`https://api.openalex.org/works?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`openalex ${res.status}`);
  const data = await res.json();
  const items = (data?.results ?? []).map(adaptOpenAlexFromClient).filter((i) => i.title);
  return { query: q, days, items, count: items.length, source: 'openalex-client' };
}

function adaptOpenAlexFromClient(w) {
  const arxivId = (w?.primary_location?.landing_page_url || '').match(/arxiv\.org\/abs\/([\w.\-/]+)/i)?.[1] ?? null;
  const ids = w?.ids ?? {};
  return {
    arxiv_id: arxivId,
    title: w?.title ?? '',
    summary: reconstructAbstract(w?.abstract_inverted_index),
    authors: (w?.authorships ?? []).map((a) => a?.author?.display_name).filter(Boolean),
    categories: [],
    primary_category: null,
    published: w?.publication_date ?? null,
    pdf_url: w?.primary_location?.pdf_url ?? (arxivId ? `https://arxiv.org/pdf/${arxivId}` : null),
    html_url: (arxivId && `https://arxiv.org/abs/${arxivId}`)
              || w?.primary_location?.landing_page_url
              || ids.doi
              || null,
  };
}

function reconstructAbstract(inv) {
  if (!inv || typeof inv !== 'object') return '';
  const slots = [];
  for (const [word, positions] of Object.entries(inv)) {
    if (!Array.isArray(positions)) continue;
    for (const pos of positions) {
      if (Number.isFinite(pos) && pos >= 0 && pos < 5000) slots[pos] = word;
    }
  }
  return slots.filter((w) => typeof w === 'string').join(' ').slice(0, 4000);
}

export async function importPapers(items) {
  const res = await fetch('/api/papers/import', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ items }),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.ok) {
    const err = new Error(body?.error?.message ?? `import ${res.status}`);
    err.code = body?.error?.code ?? 'http_error';
    err.status = res.status;
    throw err;
  }
  return body.data; // { inserted, skipped }
}

// Common date-window presets for the search UI.
export const ARXIV_DAY_PRESETS = [
  { id: 7,    labelKo: '최근 7일',   labelEn: 'Past week' },
  { id: 30,   labelKo: '최근 30일',  labelEn: 'Past month' },
  { id: 90,   labelKo: '최근 3개월', labelEn: 'Past 3 months' },
  { id: 365,  labelKo: '최근 1년',   labelEn: 'Past year' },
  { id: null, labelKo: '전체 기간',  labelEn: 'All time' },
];
